import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { customFetchV2 } from '../services/custom-fetch-v2';

type DeviceDataExportStatus = 'queued' | 'generating' | 'completed' | 'failed' | 'expired';

interface DeviceDataExport {
  id: string;
  startAt: string;
  endAt: string;
  createdAt: string;
  status: DeviceDataExportStatus;
  rowCount: number | null;
  downloadUrl: string | null;
}

const queryKey = (deviceId: string) => ['useDeviceDataExports', deviceId];
const isDeviceDataExportPreparing = (dataExport: DeviceDataExport) => (
  dataExport.status === 'queued' || dataExport.status === 'generating'
);

const getDeviceDataExports = async (deviceId: string): Promise<DeviceDataExport[]> => {
  const response = await customFetchV2<{ data: DeviceDataExport[] }>(
    `/api/v2/devices/${deviceId}/data-exports`,
    { method: 'GET' },
  );

  if (!response.ok) {
    throw new Error('Unable to load data exports');
  }

  return response.data.data;
};

const useDeviceDataExports = (deviceId: string): UseQueryResult<DeviceDataExport[], Error> => (
  useQuery(queryKey(deviceId), () => getDeviceDataExports(deviceId), {
    enabled: !!deviceId,
    retry: false,
    refetchInterval: data => data?.some(isDeviceDataExportPreparing) ? 5000 : false,
    refetchIntervalInBackground: true,
  })
);

export {
  isDeviceDataExportPreparing,
  useDeviceDataExports,
  queryKey as deviceDataExportsQueryKey,
};
export type { DeviceDataExport };
